/*
 * ANATE-PERF R9 + R13: recently-viewed rewritten (was a minified wrapper around
 * Shopify.Products.showRecentlyViewed in theme.js, which fetched products one by one).
 *
 * - R13: product fetches now run in PARALLEL (Promise.all) instead of sequentially,
 *   while injection order still follows the cookie order (most recent first).
 * - R9: when the element has data-load-margin="<px>", the fetches are deferred until
 *   the section scrolls within that distance of the viewport (IntersectionObserver).
 *   Without the attribute behavior is unchanged (fetch on connect) -- the cart page
 *   and cart drawer usages rely on that.
 *
 * Feature parity kept with the theme.js implementation:
 * - Cookie: shopify_recently_viewed, space-separated URI-encoded handles.
 * - Injects each response's [data-api-content] innerHTML into #<wrapperId>.
 * - Replaces ||itemAnimationDelay|| (index * 100) and ||itemAnimationAnchor||.
 * - Removes handles from the cookie when their product no longer resolves (404).
 * - Unhides the wrapper and dispatches theme:recently-viewed:loaded when done.
 *   (The Shopify.recentlyViewed class-juggling in theme.js was dropped: nothing in
 *   this theme ever defines Shopify.recentlyViewed, so it was dead code.)
 */
(function () {
  'use strict';

  const COOKIE_NAME = 'shopify_recently_viewed';

  const cookie = {
    read() {
      const row = document.cookie.split('; ').find((r) => r.startsWith(COOKIE_NAME + '='));
      if (!row) return [];
      const value = row.slice(COOKIE_NAME.length + 1);
      return value ? decodeURIComponent(value).split(' ') : [];
    },
    write(handles) {
      const expires = new Date(Date.now() + 90 * 24 * 3600000).toGMTString();
      document.cookie = `${COOKIE_NAME}=${encodeURIComponent(handles.join(' '))}; expires=${expires}; path=/; domain=${window.location.hostname}; sameSite=none; secure=true`;
    },
    remove(handle) {
      const handles = cookie.read();
      const position = handles.indexOf(handle);
      if (position !== -1) {
        handles.splice(position, 1);
        cookie.write(handles);
      }
    },
  };

  if (!customElements.get('recently-viewed')) {
    customElements.define(
      'recently-viewed',
      class extends HTMLElement {
        constructor() {
          super();
          this.section = this.closest('[data-section-id]');
          this.howManyToShow = parseInt(this.dataset.limit, 10) || 3;
          this.target = this.dataset.target;
          this.wrapperId = this.dataset.wrapperId || this.id;
          this.loaded = false;
        }

        connectedCallback() {
          const loadMargin = parseInt(this.dataset.loadMargin, 10);

          if (!isNaN(loadMargin) && loadMargin >= 0 && 'IntersectionObserver' in window) {
            // The element itself starts hidden (display:none has no box, so IO would
            // never fire on it) -- observe the closest visible section instead.
            const observed = this.section || this;
            const io = new IntersectionObserver(
              (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                  io.disconnect();
                  this.loadProducts();
                }
              },
              { rootMargin: `${loadMargin}px 0px ${loadMargin}px 0px` }
            );
            io.observe(observed);
          } else {
            this.loadProducts();
          }
        }

        loadProducts() {
          if (this.loaded) return;
          this.loaded = true;

          if (window.Shopify && window.Shopify.visualPreviewMode) return;

          const wrapper = document.querySelector(`#${this.wrapperId}`);
          const handles = cookie.read().slice(0, this.howManyToShow);

          if (!wrapper || handles.length === 0) return;

          const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';

          Promise.all(
            handles.map((handle) =>
              fetch(`${root}products/${handle}?section_id=${this.target}`)
                .then((response) => {
                  if (!response.ok) throw new Error(response.status);
                  return response.text();
                })
                .catch(() => {
                  // Product is gone (unpublished/deleted): prune it so we stop trying.
                  cookie.remove(handle);
                  return null;
                })
            )
          ).then((results) => {
            const aosAnchor = wrapper.id ? `#${wrapper.id}` : '';
            let shown = 0;

            results.forEach((html) => {
              if (html === null) return;
              const fresh = document.createElement('div');
              fresh.innerHTML = html
                .replaceAll('||itemAnimationDelay||', String(shown * 100))
                .replaceAll('||itemAnimationAnchor||', aosAnchor);
              const content = fresh.querySelector('[data-api-content]');
              if (content) {
                wrapper.insertAdjacentHTML('beforeend', content.innerHTML);
                shown++;
              }
            });

            wrapper.classList.remove('hidden');
            this.dispatchEvent(new CustomEvent('theme:recently-viewed:loaded', { bubbles: false }));
          });
        }
      }
    );
  }
})();
